import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { api, apiErrorMessage } from "../../api";
import { useToast } from "../../hooks/use-toast";

export default function InvoiceNew() {
  const nav = useNavigate();
  const { toast } = useToast();
  const [params] = useSearchParams();
  const preselectClientId = params.get("client_id") || "";
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [entries, setEntries] = useState([]);
  const [clientId, setClientId] = useState("");
  const [issued, setIssued] = useState(new Date().toISOString().slice(0, 10));
  const [due, setDue] = useState(() => { const d = new Date(); d.setDate(d.getDate() + 14); return d.toISOString().slice(0, 10); });
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const [c, p, e] = await Promise.all([api.clients(), api.projects(), api.entries()]);
      setClients(c); setProjects(p); setEntries(e);
      if (preselectClientId && c.some((x) => x.id === preselectClientId)) setClientId(preselectClientId);
      else if (c.length) setClientId(c[0].id);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clientProjects = useMemo(
    () => projects.filter((p) => p.client_id === clientId && p.billable),
    [projects, clientId]
  );

  useEffect(() => {
    if (!clientId) return;
    // Auto-fill items from tracked hours per billable project
    const next = clientProjects.map((p) => {
      const hrs = entries
        .filter((e) => e.project_id === p.id && e.billable)
        .reduce((a, e) => a + e.hours, 0);
      return { desc: `${p.name} — professional services`, hours: Number(hrs.toFixed(2)) || 0, rate: p.rate };
    });
    setItems(next.length ? next : [{ desc: "", hours: 1, rate: 250 }]);
  }, [clientId, entries, clientProjects]);

  const subtotal = items.reduce((a, it) => a + Number(it.hours || 0) * Number(it.rate || 0), 0);
  const total = subtotal;

  const update = (i, k, v) => {
    const next = items.slice();
    next[i] = { ...next[i], [k]: v };
    setItems(next);
  };
  const addItem = () => setItems([...items, { desc: "", hours: 1, rate: 250 }]);
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!clientId) return;
    setSaving(true);
    try {
      const inv = await api.createInvoice({
        client_id: clientId, issued, due, status: "Draft",
        items: items.map((it) => ({ desc: it.desc, hours: Number(it.hours) || 0, rate: Number(it.rate) || 0 })),
        tax: 0,
      });
      toast({ title: "Invoice created", description: `${inv.number} — $${inv.total.toLocaleString()}` });
      nav("/app/invoices");
    } catch (ex) {
      toast({ title: "Couldn't create invoice", description: apiErrorMessage(ex, "") });
    } finally { setSaving(false); }
  };

  return (
    <div>
      <Link to="/app/invoices" className="text-sm text-[#6a736f] inline-flex items-center gap-1"><ArrowLeft size={14}/> Back to invoices</Link>
      <div className="mt-2 flex items-end justify-between">
        <h1 className="font-display text-4xl">New invoice</h1>
        <div className="flex gap-2">
          <button onClick={() => nav("/app/invoices")} className="btn-outline-forest px-4 py-2 rounded-md text-sm font-medium">Cancel</button>
          <button disabled={saving} onClick={save} className="btn-coral px-4 py-2 rounded-md text-sm font-medium">
            {saving ? "Saving…" : "Save draft"}
          </button>
        </div>
      </div>

      <div className="mt-6 grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 bg-white rounded-xl border border-cream p-5">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <label>
              <span className="font-medium">Client</span>
              <select value={clientId} onChange={(e)=>setClientId(e.target.value)}
                data-testid="invoice-client-select"
                className="mt-1 w-full border border-cream rounded-md px-3 py-2">
                {clients.length === 0 && <option value="">(no clients)</option>}
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label><span className="font-medium">Issued</span>
                <input type="date" value={issued} onChange={(e)=>setIssued(e.target.value)} className="mt-1 w-full border border-cream rounded-md px-3 py-2" />
              </label>
              <label><span className="font-medium">Due</span>
                <input type="date" value={due} onChange={(e)=>setDue(e.target.value)} className="mt-1 w-full border border-cream rounded-md px-3 py-2" />
              </label>
            </div>
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between">
              <div className="font-semibold">Line items</div>
              <button onClick={addItem} className="text-sm text-coral inline-flex items-center gap-1"><Plus size={14}/> Add item</button>
            </div>
            <div className="mt-3 overflow-hidden rounded-lg border border-cream">
              <table className="w-full text-sm">
                <thead className="bg-cream-2/50 text-xs text-[#6a736f]">
                  <tr className="text-left">
                    <th className="px-3 py-2">Description</th>
                    <th className="px-3 py-2 w-24">Hours</th>
                    <th className="px-3 py-2 w-24">Rate</th>
                    <th className="px-3 py-2 w-28 text-right">Amount</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#EAF3EE]">
                  {items.map((it, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2">
                        <input value={it.desc} onChange={(e)=>update(i, "desc", e.target.value)}
                          className="w-full border border-cream rounded px-2 py-1.5" />
                      </td>
                      <td className="px-3 py-2">
                        <input type="number" min={0} step={0.25} value={it.hours} onChange={(e)=>update(i, "hours", e.target.value)}
                          className="w-full border border-cream rounded px-2 py-1.5" />
                      </td>
                      <td className="px-3 py-2">
                        <input type="number" min={0} value={it.rate} onChange={(e)=>update(i, "rate", e.target.value)}
                          className="w-full border border-cream rounded px-2 py-1.5" />
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">${(Number(it.hours||0)*Number(it.rate||0)).toLocaleString()}</td>
                      <td className="px-2"><button onClick={()=>remove(i)} className="text-[#6a736f] hover:text-coral"><Trash2 size={14}/></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-cream p-5 h-max">
          <div className="font-semibold mb-3">Summary</div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-[#6a736f]">Subtotal</span><span className="tabular-nums">${subtotal.toLocaleString()}</span></div>
            <div className="border-t border-cream pt-2 flex justify-between font-semibold">
              <span>Total</span><span className="tabular-nums text-coral">${total.toLocaleString()}</span>
            </div>
          </div>
          <p className="text-xs text-[#6a736f] mt-4">For educational purposes only, not professional financial advice.</p>
        </div>
      </div>
    </div>
  );
}
